import { useState, useEffect } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Language as LanguageIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTenant } from '../providers/TenantProvider';
import { useCustomerOnboarding } from '../hooks/useCustomerOnboarding';
import { onboardingSchema, OnboardingFormData, defaultValues } from '../lib/validations/onboarding';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

interface CustomerData {
  companyName?: string;
  companyShortName?: string;
  email?: string;
  phone?: string;
}

type TabValue = 'general' | 'address' | 'coaches' | 'schedule' | 'pricing' | 'faq';

interface TabPanelProps {
  children?: ReactNode;
  index: TabValue;
  value: TabValue;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`onboarding-tab-${index}`}
      aria-labelledby={`onboarding-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </Box>
  );
}

export const WebsiteOnboardingPage = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { onboarding, loading: onboardingLoading, error: onboardingError, saveOnboarding } = useCustomerOnboarding(tenantId || '');
  
  const [activeTab, setActiveTab] = useState<TabValue>('general');
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch customer data for pre-filling
  useEffect(() => {
    async function fetchCustomerData() {
      if (!tenantId) {
        setLoadingCustomer(false);
        return;
      }

      try {
        const customerDoc = await getDoc(doc(db, 'customers', tenantId));
        if (customerDoc.exists()) {
          const data = customerDoc.data();
          setCustomerData({
            companyName: data.companyName || '',
            companyShortName: data.companyShortName || '',
            email: data.email || '',
            phone: data.phone || '',
          });
        }
      } catch (err) {
        console.error('Error fetching customer data:', err);
      } finally {
        setLoadingCustomer(false);
      }
    }

    fetchCustomerData();
  }, [tenantId]);

  // Build initial form values
  const getInitialValues = (): Partial<OnboardingFormData> => {
    const baseValues: Partial<OnboardingFormData> = {
      ...defaultValues,
    };

    // If we have existing onboarding data, use it
    if (onboarding?.data) {
      return {
        ...baseValues,
        ...onboarding.data,
      };
    }

    // Pre-fill from customer data
    if (customerData) {
      return {
        ...baseValues,
        name: customerData.companyName || baseValues.name || '',
        shortName: customerData.companyShortName || baseValues.shortName || '',
        email: customerData.email || baseValues.email || '',
        telephone: customerData.phone || baseValues.telephone || '',
      };
    }

    return baseValues;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<OnboardingFormData>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: getInitialValues(),
    mode: 'onBlur',
  });

  // useFieldArray for coaches
  const { fields: coachFields, append: appendCoach, remove: removeCoach } = useFieldArray({
    control,
    name: 'coaches',
  });

  // Reset form when data becomes available
  useEffect(() => {
    if (!loadingCustomer && !onboardingLoading) {
      reset(getInitialValues());
    }
  }, [loadingCustomer, onboardingLoading, customerData, onboarding, reset]);

  const handleTabChange = (_event: SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate('/website');
  };

  const handleSaveDraft = async (data: OnboardingFormData) => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      await saveOnboarding(data, 'draft');
      setSaveSuccess('Progress saved successfully!');
    } catch (err) {
      console.error('Error saving draft:', err);
      setSaveError('Failed to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmit = async (data: OnboardingFormData) => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      await saveOnboarding(data, 'pending');
      setSaveSuccess('Onboarding form submitted successfully!');
      // Navigate back to dashboard after short delay
      setTimeout(() => {
        navigate('/website');
      }, 2000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setSaveError('Failed to submit form. Please try again.');
      setIsSaving(false);
    }
  };

  if (loadingCustomer || onboardingLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const tabs: { id: TabValue; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'address', label: 'Address' },
    { id: 'coaches', label: 'Coaches' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <LanguageIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" component="h1">
              Website Onboarding
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Complete the form below to set up your gym website
          </Typography>
        </Box>

        {/* Error Alert */}
        {onboardingError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {onboardingError.message}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveError}
          </Alert>
        )}

        {/* Success Alert */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {saveSuccess}
          </Alert>
        )}

        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Paper sx={{ mb: 4 }}>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                px: 2,
                pt: 2,
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  label={tab.label}
                  value={tab.id}
                  id={`onboarding-tab-${tab.id}`}
                  aria-controls={`onboarding-tab-${tab.id}`}
                />
              ))}
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* General Tab */}
              <TabPanel value={activeTab} index="general">
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      General Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Basic information about your gym
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                      {/* Gym Name */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Gym Name *"
                          {...register('name')}
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          placeholder="Enter your gym's full name"
                        />
                      </Grid>

                      {/* Short Name */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Short Name *"
                          {...register('shortName')}
                          error={!!errors.shortName}
                          helperText={errors.shortName?.message}
                          placeholder="e.g., AGA"
                        />
                      </Grid>

                      {/* Description */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Description *"
                          {...register('description')}
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          placeholder="Describe your gym, its mission, and what makes it unique"
                        />
                      </Grid>

                      {/* Website URL */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Website URL *"
                          {...register('url')}
                          error={!!errors.url}
                          helperText={errors.url?.message}
                          placeholder="https://yourgym.com"
                        />
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Email *"
                          {...register('email')}
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          placeholder="contact@yourgym.com"
                        />
                      </Grid>

                      {/* Logo URL */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Logo URL *"
                          {...register('logo')}
                          error={!!errors.logo}
                          helperText={errors.logo?.message}
                          placeholder="https://yourgym.com/logo.png"
                        />
                      </Grid>

                      {/* Image URL */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Hero Image URL *"
                          {...register('image')}
                          error={!!errors.image}
                          helperText={errors.image?.message}
                          placeholder="https://yourgym.com/hero.jpg"
                        />
                      </Grid>

                      {/* Phone */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone (with +1) *"
                          {...register('telephone')}
                          error={!!errors.telephone}
                          helperText={errors.telephone?.message || 'Format: +1234567890'}
                          placeholder="+1234567890"
                        />
                      </Grid>

                      {/* Phone Display */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone (Display Format) *"
                          {...register('phoneDisplay')}
                          error={!!errors.phoneDisplay}
                          helperText={errors.phoneDisplay?.message || 'How the phone number appears on the website'}
                          placeholder="(123) 456-7890"
                        />
                      </Grid>

                      {/* Location */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Location *"
                          {...register('location')}
                          error={!!errors.location}
                          helperText={errors.location?.message}
                          placeholder="City, State"
                        />
                      </Grid>

                      {/* Maps Embed */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Google Maps Embed URL"
                          {...register('mapsEmbed')}
                          error={!!errors.mapsEmbed}
                          helperText={errors.mapsEmbed?.message || 'Paste the iframe embed code from Google Maps'}
                          placeholder="<iframe src=...></iframe>"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Social Links Section */}
                <Card variant="outlined" sx={{ mt: 4 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Social Links
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Connect your social media accounts (optional)
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                      {/* Instagram */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Instagram"
                          {...register('instagram')}
                          error={!!errors.instagram}
                          helperText={errors.instagram?.message}
                          placeholder="https://instagram.com/yourgym"
                        />
                      </Grid>

                      {/* Facebook */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Facebook"
                          {...register('facebook')}
                          error={!!errors.facebook}
                          helperText={errors.facebook?.message}
                          placeholder="https://facebook.com/yourgym"
                        />
                      </Grid>

                      {/* YouTube */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="YouTube"
                          {...register('youtube')}
                          error={!!errors.youtube}
                          helperText={errors.youtube?.message}
                          placeholder="https://youtube.com/yourgym"
                        />
                      </Grid>

                      {/* Patreon */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Patreon"
                          {...register('patreon')}
                          error={!!errors.patreon}
                          helperText={errors.patreon?.message}
                          placeholder="https://patreon.com/yourgym"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Placeholder tabs for future stories */}
              <TabPanel value={activeTab} index="address">
                <Alert severity="info">
                  Address tab coming in the next story. Please complete the General tab first.
                </Alert>
              </TabPanel>

              <TabPanel value={activeTab} index="coaches">
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Coaches
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => appendCoach({ name: '', rank: '', role: '', bio: '', specialties: [], photo: '' })}
                        size="small"
                      >
                        Add Coach
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Add your gym's coaches. Each coach needs a name, rank, role, bio, and optionally specialties and a photo.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {/* At least one coach validation */}
                    {errors.coaches?.root && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {errors.coaches.root.message || 'At least one coach is required'}
                      </Alert>
                    )}

                    {/* Coaches List */}
                    <Grid container spacing={3}>
                      {coachFields.length === 0 ? (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            No coaches added yet. Click "Add Coach" to add your first coach.
                          </Alert>
                        </Grid>
                      ) : (
                        coachFields.map((field, index) => (
                          <Grid item xs={12} md={6} key={field.id}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Coach #{index + 1}
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => removeCoach(index)}
                                  >
                                    Remove
                                  </Button>
                                </Box>

                                <Grid container spacing={2}>
                                  {/* Name */}
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Name *"
                                      {...register(`coaches.${index}.name` as const)}
                                      error={!!errors.coaches?.[index]?.name}
                                      helperText={errors.coaches?.[index]?.name?.message}
                                      placeholder="Coach name"
                                    />
                                  </Grid>

                                  {/* Rank */}
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Rank *"
                                      {...register(`coaches.${index}.rank` as const)}
                                      error={!!errors.coaches?.[index]?.rank}
                                      helperText={errors.coaches?.[index]?.rank?.message}
                                      placeholder="e.g., Black Belt"
                                    />
                                  </Grid>

                                  {/* Role */}
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Role *"
                                      {...register(`coaches.${index}.role` as const)}
                                      error={!!errors.coaches?.[index]?.role}
                                      helperText={errors.coaches?.[index]?.role?.message}
                                      placeholder="e.g., Head Instructor"
                                    />
                                  </Grid>

                                  {/* Specialties */}
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Specialties"
                                      {...register(`coaches.${index}.specialties` as const)}
                                      helperText="Comma-separated list (e.g., Self Defense, Competition, Kids)"
                                      placeholder="Self Defense, Competition, Kids"
                                    />
                                  </Grid>

                                  {/* Bio */}
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={3}
                                      size="small"
                                      label="Bio"
                                      {...register(`coaches.${index}.bio` as const)}
                                      error={!!errors.coaches?.[index]?.bio}
                                      helperText={errors.coaches?.[index]?.bio?.message}
                                      placeholder="Short biography"
                                    />
                                  </Grid>

                                  {/* Photo */}
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Photo URL"
                                      {...register(`coaches.${index}.photo` as const)}
                                      error={!!errors.coaches?.[index]?.photo}
                                      helperText={errors.coaches?.[index]?.photo?.message}
                                      placeholder="https://yourgym.com/coach-photo.jpg"
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </TabPanel>

              <TabPanel value={activeTab} index="schedule">
                <Alert severity="info">
                  Schedule tab coming in the next story. Please complete the General tab first.
                </Alert>
              </TabPanel>

              <TabPanel value={activeTab} index="pricing">
                <Alert severity="info">
                  Pricing tab coming in the next story. Please complete the General tab first.
                </Alert>
              </TabPanel>

              <TabPanel value={activeTab} index="faq">
                <Alert severity="info">
                  FAQ tab coming in the next story. Please complete the General tab first.
                </Alert>
              </TabPanel>
            </Box>

            {/* Form Actions */}
            <Box
              sx={{
                p: 3,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit(handleSaveDraft)}
                  disabled={isSaving}
                >
                  Save Draft
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={20} /> : null}
              >
                {isSaving ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default WebsiteOnboardingPage;
